import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useToast } from "../context/ToastContext";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolFileUploadField from "./shared/ToolFileUploadField";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";
import { extractErrorMessage } from "../utils/errorUtils";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import {
  downloadPolicyFile,
  getParsedStyles,
  listPolicies,
  listVendors,
  uploadPolicy,
  upsertVendor,
} from "../services/mapPolicyApi";
import "./ToolPage.css";
import "./MapPolicyTool.css";


const VIEW_VENDORS = "vendors";
const VIEW_VENDOR_DETAIL = "vendor-detail";

const STATUS_LABEL = {
  active: "Active",
  upcoming: "Upcoming",
  expired: "Expired",
  superseded: "Superseded",
};

const STATUS_COLOR = {
  active: "var(--success)",
  upcoming: "var(--info)",
  expired: "var(--text-muted)",
  superseded: "var(--accent-purple)",
};


function MapPolicyTool() {
  const { addToast } = useToast();
  const [view, setView] = useState(VIEW_VENDORS);
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState(null);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const toolInfo = useMemo(
    () => ({
      toolName: "MAP Policies",
      description:
        "Upload vendor MAP policy files into the portal. Each policy is parsed, stored alongside its effective window, and browseable from this page.",
      steps: [
        "Open the vendor whose policy you're uploading. If the vendor isn't listed yet, click Add Vendor.",
        "Click Upload Policy and choose the formatted .xlsx file. The file must follow the Heel MAP Policy template (Metadata + Styles sheets).",
        "After upload the policy appears in this vendor's policy list with its effective window and parsed style count.",
        "Click any policy to view its parsed styles or to re-download the original file.",
      ],
      templates: [],
    }),
    []
  );

  const fetchVendors = useCallback(async () => {
    setLoadingVendors(true);
    try {
      const data = await listVendors();
      setVendors(data);
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to load vendors.");
      addToast(message, "error");
    } finally {
      setLoadingVendors(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const handleVendorSelect = (vendorId) => {
    setSelectedVendorId(vendorId);
    setView(VIEW_VENDOR_DETAIL);
  };

  const handleBackToVendors = () => {
    setSelectedVendorId(null);
    setView(VIEW_VENDORS);
  };

  return (
    <>
      <ToolPageLayout
        title="MAP Policies"
        onOpenInfo={() => setShowInfoModal(true)}
      >
        {view === VIEW_VENDORS ? (
          <VendorsListView
            vendors={vendors}
            loading={loadingVendors}
            onSelect={handleVendorSelect}
            onAddVendorClick={() => setShowAddVendor(true)}
            onRefresh={fetchVendors}
          />
        ) : (
          <VendorDetailView
            vendorId={selectedVendorId}
            vendor={vendors.find((v) => v.vendorId === selectedVendorId)}
            onBack={handleBackToVendors}
          />
        )}

        {showAddVendor ? (
          <AddVendorModal
            onClose={() => setShowAddVendor(false)}
            onCreated={async (vendor) => {
              setShowAddVendor(false);
              await fetchVendors();
              if (vendor?.vendorId) {
                setSelectedVendorId(vendor.vendorId);
                setView(VIEW_VENDOR_DETAIL);
              }
            }}
          />
        ) : null}
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


function VendorsListView({ vendors, loading, onSelect, onAddVendorClick, onRefresh }) {
  return (
    <div className="tool-form-main">
      <section className="tool-section">
        <div className="tool-section-header map-section-header-row">
          <h3 className="tool-section-title">Vendors</h3>
          <div className="map-header-actions">
            <button type="button" className="tool-btn-secondary" onClick={onRefresh} disabled={loading}>
              {loading ? "Refreshing…" : "Refresh"}
            </button>
            <button type="button" className="tool-btn-primary map-btn-inline" onClick={onAddVendorClick}>
              + Add Vendor
            </button>
          </div>
        </div>

        {loading && vendors.length === 0 ? (
          <p className="tool-helper-text">Loading vendors…</p>
        ) : vendors.length === 0 ? (
          <p className="tool-helper-text">
            No vendors registered yet. Click <strong>Add Vendor</strong> to register one
            (e.g. Oboz, New Balance).
          </p>
        ) : (
          <div className="map-card-grid">
            {vendors.map((vendor) => (
              <button
                key={vendor.vendorId}
                type="button"
                onClick={() => onSelect(vendor.vendorId)}
                className="map-vendor-card"
              >
                <div className="map-vendor-card-body">
                  <div className="map-vendor-name">{vendor.displayName}</div>
                  {vendor.notes ? (
                    <div className="map-vendor-meta">{vendor.notes}</div>
                  ) : null}
                </div>
                <span className="map-vendor-open">Open →</span>
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}


function VendorDetailView({ vendorId, vendor, onBack }) {
  const { addToast } = useToast();
  const [policies, setPolicies] = useState([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [expandedPolicyId, setExpandedPolicyId] = useState(null);
  const [parsedStylesByPolicy, setParsedStylesByPolicy] = useState({});
  const [loadingStylesForPolicyId, setLoadingStylesForPolicyId] = useState(null);
  const [downloadingPolicyId, setDownloadingPolicyId] = useState(null);

  const fetchPolicies = useCallback(async () => {
    if (!vendorId) return;
    setLoadingPolicies(true);
    try {
      const data = await listPolicies(vendorId, { includeExpired: true });
      setPolicies(data);
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to load policies.");
      addToast(message, "error");
    } finally {
      setLoadingPolicies(false);
    }
  }, [vendorId, addToast]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!uploadFile) {
      addToast("Please choose a file.", "error");
      return;
    }
    const maxSize = 100 * 1024 * 1024;
    if (uploadFile.size > maxSize) {
      addToast("File is too large. Maximum size is 100MB.", "error");
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const policy = await uploadPolicy(vendorId, uploadFile, {
        onUploadProgress: (progressEvent) => {
          const pct = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1)
          );
          setUploadProgress(pct);
        },
      });
      addToast(`Uploaded ${policy?.policyName || "policy"}.`, "success");
      setUploadFile(null);
      await fetchPolicies();
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to upload policy.");
      addToast(message, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleExpand = async (policy) => {
    if (expandedPolicyId === policy.policyId) {
      setExpandedPolicyId(null);
      return;
    }
    setExpandedPolicyId(policy.policyId);
    if (parsedStylesByPolicy[policy.policyId]) return;
    setLoadingStylesForPolicyId(policy.policyId);
    try {
      const data = await getParsedStyles(policy.policyId);
      setParsedStylesByPolicy((prev) => ({ ...prev, [policy.policyId]: data }));
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to load parsed styles.");
      addToast(message, "error");
    } finally {
      setLoadingStylesForPolicyId(null);
    }
  };

  const handleFileDownload = async (policy) => {
    setDownloadingPolicyId(policy.policyId);
    try {
      const { data, contentDisposition } = await downloadPolicyFile(policy.policyId);
      const blob = new Blob([data], {
        type:
          policy.fileMimeType ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const extracted = extractFilenameFromHeader(contentDisposition);
      link.setAttribute(
        "download",
        extracted || policy.fileName || `policy_${policy.policyId}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to download file.");
      addToast(message, "error");
    } finally {
      setDownloadingPolicyId(null);
    }
  };

  if (!vendor) {
    return (
      <div className="tool-form-main">
        <p className="tool-helper-text">Loading vendor…</p>
        <button type="button" className="tool-btn-secondary" onClick={onBack}>
          ← All vendors
        </button>
      </div>
    );
  }

  return (
    <div className="tool-form-main">
      <div className="map-back-row">
        <button type="button" className="tool-link-button" onClick={onBack}>
          ← All vendors
        </button>
      </div>

      <section className="tool-section">
        <div className="tool-section-header">
          <h3 className="tool-section-title">{vendor.displayName}</h3>
        </div>
        {vendor.notes ? (
          <p className="tool-helper-text">{vendor.notes}</p>
        ) : null}
        {vendor.buyerEmails && vendor.buyerEmails.length > 0 ? (
          <p className="tool-helper-text">
            Buyer emails: {vendor.buyerEmails.join(", ")}
          </p>
        ) : null}
      </section>

      <section className="tool-section">
        <div className="tool-section-header">
          <h3 className="tool-section-title">Upload New Policy</h3>
        </div>
        <form onSubmit={handleUpload}>
          <ToolFileUploadField
            label="Policy file (.xlsx, Heel template)"
            file={uploadFile}
            setFile={setUploadFile}
            accept=".xlsx"
            helperText="The workbook must follow the Heel MAP Policy template — two sheets named Metadata and Styles. See MAP_POLICY_TEMPLATE.md."
          />
          <div className="tool-action-stack">
            <button type="submit" className="tool-btn-primary" disabled={uploading}>
              {uploading ? "Uploading…" : "Upload Policy"}
            </button>
            <ToolProgressPanel
              loading={uploading}
              uploadProgress={uploadProgress}
              uploadMessage="Uploading policy file…"
              processingMessage="Parsing and storing…"
            />
          </div>
        </form>
      </section>

      <section className="tool-section">
        <div className="tool-section-header map-section-header-row">
          <h3 className="tool-section-title">Policies</h3>
          <button
            type="button"
            className="tool-btn-secondary"
            onClick={fetchPolicies}
            disabled={loadingPolicies}
          >
            {loadingPolicies ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {policies.length === 0 ? (
          <p className="tool-helper-text">
            {loadingPolicies ? "Loading…" : "No policies uploaded yet for this vendor."}
          </p>
        ) : (
          <div className="map-card-grid">
            {policies.map((policy) => {
              const isExpanded = expandedPolicyId === policy.policyId;
              const parsed = parsedStylesByPolicy[policy.policyId];
              return (
                <div
                  key={policy.policyId}
                  className={`map-policy-card ${isExpanded ? "map-policy-card--expanded" : ""}`}
                >
                  <div className="map-policy-card-top">
                    <div className="map-policy-card-info">
                      <div className="map-policy-name">{policy.policyName}</div>
                      <div className="map-policy-dates">
                        {policy.effectiveStartDate} → {policy.effectiveEndDate}
                      </div>
                      <div className="map-policy-pill-row">
                        <StatusPill status={policy.status} />
                      </div>
                      {policy.notes ? (
                        <div className="map-policy-notes">{policy.notes}</div>
                      ) : null}
                    </div>
                    <div className="map-policy-card-actions">
                      <button
                        type="button"
                        className="tool-btn-secondary"
                        onClick={() => handleFileDownload(policy)}
                        disabled={downloadingPolicyId === policy.policyId}
                      >
                        {downloadingPolicyId === policy.policyId
                          ? "Downloading…"
                          : "Download original"}
                      </button>
                      <button
                        type="button"
                        className="tool-btn-secondary"
                        onClick={() => handleExpand(policy)}
                      >
                        {isExpanded ? "Hide styles" : "View styles"}
                      </button>
                    </div>
                  </div>

                  {isExpanded ? (
                    <div className="map-policy-expanded">
                      {loadingStylesForPolicyId === policy.policyId ? (
                        <p className="tool-helper-text">Loading styles…</p>
                      ) : parsed ? (
                        <ParsedStylesTable parsed={parsed} />
                      ) : (
                        <p className="tool-helper-text">No styles loaded.</p>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}


function StatusPill({ status }) {
  const label = STATUS_LABEL[status] || status || "Unknown";
  const color = STATUS_COLOR[status] || "var(--text-muted)";
  return (
    <span
      className="map-status-pill"
      style={{ "--pill-color": color }}
    >
      <span className="map-status-dot" />
      {label}
    </span>
  );
}


function ParsedStylesTable({ parsed }) {
  const styles = parsed?.styles || [];
  if (styles.length === 0) {
    return <p className="tool-helper-text">No parsed styles in this policy.</p>;
  }

  return (
    <div>
      <p className="tool-helper-text map-styles-count">
        {styles.length} parsed style{styles.length === 1 ? "" : "s"} (version{" "}
        {parsed?.version || 1}). Read-only in this PR.
      </p>
      <div className="map-table-scroll">
        <table className="map-table">
          <thead>
            <tr>
              <th>Vendor SKU</th>
              <th>Style</th>
              <th>Color</th>
              <th>Gender</th>
              <th>Cost</th>
              <th>MSRP</th>
              <th>MAP</th>
              <th>Step Down</th>
              <th>Disc.</th>
            </tr>
          </thead>
          <tbody>
            {styles.map((style) => (
              <tr key={style.rowId}>
                <td>
                  <code className="map-sku-code">{style.vendorSku}</code>
                  {style.colorCode ? <span className="map-color-code">-{style.colorCode}</span> : null}
                </td>
                <td>{style.styleName || "—"}</td>
                <td>{style.color || "—"}</td>
                <td>{style.gender || "—"}</td>
                <td className="map-td-mono">{formatMoney(style.cost)}</td>
                <td className="map-td-mono">{formatMoney(style.msrp)}</td>
                <td className="map-td-mono">{formatMoney(style.mapPrice)}</td>
                <td>
                  {style.stepDownDate
                    ? `${style.stepDownDate}${
                        style.stepDownMap != null ? ` → ${formatMoney(style.stepDownMap)}` : ""
                      }`
                    : "—"}
                </td>
                <td>{style.discontinued ? "Yes" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function AddVendorModal({ onClose, onCreated }) {
  const { addToast } = useToast();
  const [displayName, setDisplayName] = useState("");
  const [buyerEmails, setBuyerEmails] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const name = displayName.trim();
    if (!name) {
      addToast("Display name is required.", "error");
      return;
    }
    setSaving(true);
    try {
      const vendor = await upsertVendor({
        displayName: name,
        buyerEmails: buyerEmails
          .split(/[,\s]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        notes: notes.trim() || undefined,
      });
      addToast(`Saved vendor ${vendor?.displayName || name}.`, "success");
      onCreated?.(vendor);
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to save vendor.");
      addToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="map-modal-backdrop" onClick={onClose}>
      <div className="map-modal" onClick={(event) => event.stopPropagation()}>
        <div className="map-modal-header">
          <h3 className="map-modal-title">Add Vendor</h3>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="map-modal-body">
            <div className="tool-field-stack">
              <label className="tool-label">Display name</label>
              <p className="tool-helper-text">
                Must match the Shopify <code>vendor</code> field exactly.
              </p>
              <input
                type="text"
                className="tool-input"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                required
              />
            </div>
            <div className="tool-field-stack">
              <label className="tool-label">Buyer emails (optional)</label>
              <p className="tool-helper-text">Comma-separated. Used by future expiry notifications.</p>
              <input
                type="text"
                className="tool-input"
                value={buyerEmails}
                onChange={(event) => setBuyerEmails(event.target.value)}
              />
            </div>
            <div className="tool-field-stack">
              <label className="tool-label">Notes (optional)</label>
              <textarea
                className="tool-input"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                rows={3}
                placeholder="How this vendor sends MAP files (email, B2B, etc.)"
              />
            </div>
          </div>
          <div className="map-modal-actions">
            <button type="button" className="tool-btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="tool-btn-primary map-btn-inline" disabled={saving}>
              {saving ? "Saving…" : "Save Vendor"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function formatMoney(value) {
  if (value == null) return "—";
  if (typeof value !== "number") return value;
  return `$${value.toFixed(2)}`;
}


export default MapPolicyTool;
